import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  WRITE_OFF_STATUSES,
  type CreateWriteOffInput,
  type WriteOffActDto,
  type WriteOffListRow,
  type WriteOffStatus,
} from '@eombor/contracts';
import type { ApiClientError } from '../../../shared/api/client.js';
import { writeOffApi, type WriteOffPage, type WriteOffQuery } from '../api/write-off.api.js';
import { TAB_STATUSES, type WriteOffTab } from './write-off-tabs.js';

export const writeOffKeys = {
  mine: (query: WriteOffQuery) => ['write-offs', 'mine', query] as const,
  queue: (query: WriteOffQuery) => ['write-offs', 'queue', query] as const,
  one: (id: string) => ['write-offs', 'one', id] as const,
};

export const useMyWriteOffs = (
  query: WriteOffQuery = {},
): UseQueryResult<WriteOffPage, ApiClientError> =>
  useQuery<WriteOffPage, ApiClientError>({
    queryKey: writeOffKeys.mine(query),
    queryFn: () => writeOffApi.listMine(query),
    retry: false,
  });

export const useWriteOffQueue = (
  query: WriteOffQuery = {},
): UseQueryResult<WriteOffPage, ApiClientError> =>
  useQuery<WriteOffPage, ApiClientError>({
    queryKey: writeOffKeys.queue(query),
    queryFn: () => writeOffApi.listQueue(query),
    retry: false,
  });

/** Navbat tartibi serverdagidek: kutilayotganlar birinchi, keyin yangisi tepada. */
const byQueueOrder = (a: WriteOffListRow, b: WriteOffListRow): number => {
  const pending = Number(b.status === 'PENDING') - Number(a.status === 'PENDING');
  if (pending !== 0) return pending;
  return b.submittedAt.localeCompare(a.submittedAt);
};

export interface WriteOffTabList {
  isPending: boolean;
  isError: boolean;
  error: ApiClientError | null;
  items: WriteOffListRow[];
  total: number;
  refetch: () => void;
}

/**
 * Bitta TABning ro'yxati.
 *
 * API `status` ga faqat BITTA qiymat oladi, «Rad etilgan» tabi esa ikkita
 * holatni ko'rsatadi — shuning uchun har holat alohida so'raladi va
 * natijalar shu yerda birlashtiriladi. API ham, sxema ham o'zgarmaydi.
 * `ALL` — filtrsiz bitta so'rov.
 */
export const useWriteOffTabList = (tab: WriteOffTab): WriteOffTabList =>
  useQueries({
    queries: (tab === 'ALL' ? [{}] : TAB_STATUSES[tab].map((status) => ({ status }))).map(
      (query: WriteOffQuery) => ({
        queryKey: writeOffKeys.queue(query),
        queryFn: () => writeOffApi.listQueue(query),
        retry: false,
      }),
    ),
    combine: (results) => {
      const failed = results.find((result) => result.isError);
      return {
        isPending: results.some((result) => result.isPending),
        isError: failed !== undefined,
        error: (failed?.error as ApiClientError | undefined) ?? null,
        items: results.flatMap((result) => result.data?.items ?? []).sort(byQueueOrder),
        total: results.reduce((sum, result) => sum + (result.data?.total ?? 0), 0),
        refetch: () => {
          for (const result of results) void result.refetch();
        },
      };
    },
  });

/**
 * Har HOLATning umumiy soni — tab hisoblagichlari uchun.
 *
 * `limit=1`: bizga faqat `total` kerak. Kalitlar `['write-offs', ...]`
 * ostida, ya'ni tasdiqlash/rad etishdan keyin `invalidateAll` ularni
 * ham yangilaydi.
 */
export const useWriteOffStatusTotals = (): Partial<Record<WriteOffStatus, number>> =>
  useQueries({
    queries: WRITE_OFF_STATUSES.map((status) => {
      const query: WriteOffQuery = { status, limit: 1 };
      return {
        queryKey: writeOffKeys.queue(query),
        queryFn: () => writeOffApi.listQueue(query),
        retry: false,
      };
    }),
    combine: (results) => {
      const totals: Partial<Record<WriteOffStatus, number>> = {};
      results.forEach((result, index) => {
        const status = WRITE_OFF_STATUSES[index];
        if (status !== undefined && result.data !== undefined) totals[status] = result.data.total;
      });
      return totals;
    },
  });

export const useWriteOff = (
  id: string | null,
): UseQueryResult<WriteOffActDto, ApiClientError> =>
  useQuery<WriteOffActDto, ApiClientError>({
    queryKey: writeOffKeys.one(id ?? ''),
    queryFn: () => writeOffApi.get(id as string),
    enabled: id !== null,
    retry: false,
  });

/**
 * Rasxoddan keyin YANGILANADIGAN ro'yxatlar.
 *
 * Qoldiq ham shu yerda: akt yuborilganda kartochkadagi «rasxodda» soni,
 * tasdiqlanganda esa qoldiqning O'ZI o'zgaradi. U yangilanmasa, xodim
 * eski sonni ko'rib turib ikkinchi aktni yuborardi va "yetarli qoldiq
 * yo'q" degan tushunarsiz javob olardi.
 */
const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>): void => {
  void queryClient.invalidateQueries({ queryKey: ['write-offs'] });
  void queryClient.invalidateQueries({ queryKey: ['subdivision'] });
};

/**
 * AKT YUBORADI — fayllar avval, akt keyin.
 *
 * Fayllar ketma-ket emas, BIRGA yuklanadi (`Promise.all`): telefon
 * internetida uchta rasm ketma-ket ketsa, xodim tugma oldida uzoq
 * kutardi. Akt esa ular tugagach yuboriladi — u fayl `id` lariga
 * tayanadi.
 */
export const useCreateWriteOff = () => {
  const queryClient = useQueryClient();

  return useMutation<
    WriteOffActDto,
    ApiClientError,
    Omit<CreateWriteOffInput, 'actFileId' | 'photoFileIds'> & {
      actFile: File;
      photos: File[];
    }
  >({
    mutationFn: async ({ actFile, photos, ...rest }) => {
      const [act, ...uploaded] = await Promise.all([
        writeOffApi.upload(actFile, 'WRITE_OFF_ACT'),
        ...photos.map((photo) => writeOffApi.upload(photo, 'WRITE_OFF_PHOTO')),
      ]);

      return writeOffApi.create({
        ...rest,
        actFileId: act.id,
        photoFileIds: uploaded.map((file) => file.id),
      });
    },
    onSuccess: () => invalidateAll(queryClient),
  });
};

export interface WriteOffDecisionInput {
  id: string;
  action: 'approve' | 'reject' | 'resubmit' | 'cancel';
  /** Faqat `reject` uchun — boshqa amallarda e'tiborga olinmaydi. */
  reason?: string;
}

/**
 * To'rt amal BITTA mutatsiyada.
 *
 * Ular bir xil javobni qaytaradi va bir xil ro'yxatlarni eskirtiradi;
 * to'rtta alohida hook yozilsa, `onSuccess` mantig'i to'rt marta
 * takrorlanardi va bir kun ulardan biri qoldiqni yangilashni unutardi.
 */
export const useReviewWriteOff = () => {
  const queryClient = useQueryClient();

  return useMutation<WriteOffActDto, ApiClientError, WriteOffDecisionInput>({
    mutationFn: ({ id, action, reason }) => {
      switch (action) {
        case 'approve':
          return writeOffApi.approve(id);
        case 'reject':
          return writeOffApi.reject(id, reason ?? '');
        case 'resubmit':
          return writeOffApi.resubmit(id);
        case 'cancel':
          return writeOffApi.cancel(id);
      }
    },
    onSuccess: () => invalidateAll(queryClient),
  });
};
